import { createFileRoute, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, Check, X, Eye, MessageSquarePlus, Loader2, Search,
  ArrowUpDown, RefreshCw, Inbox,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { useServerFn as _u } from "@tanstack/react-start";
import { listAnomalyInbox, transitionAnomaly, addAnomalyNote, getAnomalyContacts } from "@/lib/anomaly-inbox.functions";
import { runAnomalyDetectorsForAccount } from "@/lib/anomaly-detectors.functions";
import { aiAnomalyExplanation } from "@/lib/ai.functions";
import { EmptyState as SharedEmptyState } from "@/components/empty-state";

export const Route = createFileRoute("/_authenticated/workspaces/$workspaceId/anomalies")({
  component: WorkspaceAnomaliesPage,
});

function WorkspaceAnomaliesPage() {
  const { workspaceId } = useParams({ from: "/_authenticated/workspaces/$workspaceId/anomalies" });
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Anomalies</h1>
        <p className="text-sm text-muted-foreground">Things worth a second look in this workspace.</p>
      </div>
      <AnomalyInbox workspaceId={workspaceId} />
    </div>
  );
}

const SEV_STYLES: Record<string, { stripe: string; badge: string; label: string }> = {
  critical: { stripe: "bg-red-500", badge: "bg-red-500/15 text-red-700 border-red-500/30", label: "Critical" },
  high:     { stripe: "bg-orange-500", badge: "bg-orange-500/15 text-orange-700 border-orange-500/30", label: "High" },
  medium:   { stripe: "bg-amber-400", badge: "bg-amber-400/15 text-amber-700 border-amber-400/30", label: "Medium" },
  low:      { stripe: "bg-sky-400", badge: "bg-sky-400/15 text-sky-700 border-sky-400/30", label: "Low" },
};

const STATUS_LABEL: Record<string, string> = {
  pending: "Open",
  confirmed: "Resolved",
  rejected: "Dismissed",
};

type Filters = {
  statuses: ("pending" | "confirmed" | "rejected")[];
  severities: ("low" | "medium" | "high" | "critical")[];
  time_range: "24h" | "7d" | "30d" | "all";
  search: string;
  sort_by: "detected_at" | "severity" | "amount" | "workspace";
  sort_dir: "asc" | "desc";
};

const DEFAULT_FILTERS: Filters = {
  statuses: ["pending"],
  severities: [],
  time_range: "30d",
  search: "",
  sort_by: "severity",
  sort_dir: "desc",
};

export function AnomalyInbox({ workspaceId }: { workspaceId?: string }) {
  const fetchInbox = useServerFn(listAnomalyInbox);
  const runScan = useServerFn(runAnomalyDetectorsForAccount);
  const enhance = useServerFn(aiAnomalyExplanation);
  const qc = useQueryClient();
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [openId, setOpenId] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["anomaly-inbox", workspaceId ?? "all", filters],
    queryFn: () => fetchInbox({ data: { workspace_id: workspaceId ?? null, ...filters } }),
  });

  const items = (data?.anomalies ?? []) as any[];
  const open = useMemo(() => items.find((a) => a.id === openId) ?? null, [items, openId]);

  // Lazy AI enhancement: for any pending anomaly missing explanation_body, generate + persist.
  useEffect(() => {
    items.forEach((a) => {
      if (a.explanation_body || a.status !== "pending") return;
      enhance({
        data: {
          anomaly_id: a.id,
          anomaly_type: a.anomaly_type,
          default_title: a.title ?? `Detected ${a.anomaly_type}`,
          severity: a.severity,
          workspace_name: a.workspaces?.program_name,
          detection_payload: a.detection_payload ?? {},
        },
      }).then(() => qc.invalidateQueries({ queryKey: ["anomaly-inbox"] })).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((a) => a.id).join(",")]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res: any = await runScan();
      toast.success(`Scan complete, ${res.inserted ?? 0} new ${res.inserted === 1 ? "anomaly" : "anomalies"}.`);
      await refetch();
    } catch (e: any) {
      toast.error(e?.message ?? "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="space-y-4">
      <FiltersBar filters={filters} setFilters={setFilters} onScan={handleScan} scanning={scanning} />

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading anomalies…
        </div>
      ) : items.length === 0 ? (
        <EmptyState filters={filters} />
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <AnomalyRow key={a.id} a={a} onOpen={() => setOpenId(a.id)} />
          ))}
        </div>
      )}

      <DetailSheet
        anomaly={open}
        onClose={() => setOpenId(null)}
        onChanged={() => qc.invalidateQueries({ queryKey: ["anomaly-inbox"] })}
      />
    </div>
  );
}

function FiltersBar({
  filters, setFilters, onScan, scanning,
}: {
  filters: Filters;
  setFilters: (f: Filters) => void;
  onScan: () => void;
  scanning: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card p-3 flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          placeholder="Search title, body, type…"
          className="pl-8 h-9"
        />
      </div>

      <Select
        value={filters.statuses[0] ?? "pending"}
        onValueChange={(v) => setFilters({ ...filters, statuses: [v as any] })}
      >
        <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="pending">Open</SelectItem>
          <SelectItem value="confirmed">Resolved</SelectItem>
          <SelectItem value="rejected">Dismissed</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.severities[0] ?? "all"}
        onValueChange={(v) => setFilters({ ...filters, severities: v === "all" ? [] : [v as any] })}
      >
        <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Severity" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All severities</SelectItem>
          <SelectItem value="critical">Critical</SelectItem>
          <SelectItem value="high">High</SelectItem>
          <SelectItem value="medium">Medium</SelectItem>
          <SelectItem value="low">Low</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.time_range}
        onValueChange={(v) => setFilters({ ...filters, time_range: v as any })}
      >
        <SelectTrigger className="h-9 w-[140px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="24h">Last 24h</SelectItem>
          <SelectItem value="7d">Last 7 days</SelectItem>
          <SelectItem value="30d">Last 30 days</SelectItem>
          <SelectItem value="all">All time</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={`${filters.sort_by}:${filters.sort_dir}`}
        onValueChange={(v) => {
          const [sort_by, sort_dir] = v.split(":") as [Filters["sort_by"], Filters["sort_dir"]];
          setFilters({ ...filters, sort_by, sort_dir });
        }}
      >
        <SelectTrigger className="h-9 w-[180px]">
          <ArrowUpDown className="h-3.5 w-3.5 mr-1" /><SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="severity:desc">Severity (high → low)</SelectItem>
          <SelectItem value="detected_at:desc">Newest first</SelectItem>
          <SelectItem value="detected_at:asc">Oldest first</SelectItem>
          <SelectItem value="amount:desc">Amount at risk (high → low)</SelectItem>
          <SelectItem value="workspace:asc">Workspace (A → Z)</SelectItem>
        </SelectContent>
      </Select>

      <Button variant="outline" size="sm" onClick={onScan} disabled={scanning} className="ml-auto">
        {scanning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
        Run scan
      </Button>
    </div>
  );
}

function AnomalyRow({ a, onOpen }: { a: any; onOpen: () => void }) {
  const sev = SEV_STYLES[a.severity] ?? SEV_STYLES.medium;
  const body = a.explanation_body ?? "Writing the brief…";
  return (
    <Card className="overflow-hidden">
      <div className="flex">
        <div className={`w-1 shrink-0 ${sev.stripe}`} />
        <div className="flex-1 p-4 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <Badge variant="outline" className={sev.badge}>{sev.label}</Badge>
              {a.workspaces?.program_name && (
                <Badge variant="outline" className="font-normal">{a.workspaces.program_name}</Badge>
              )}
              <span className="text-xs text-muted-foreground capitalize">
                {a.anomaly_type.replaceAll("_", " ")}
              </span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">
                {a.affected_contact_ids?.length ?? 0} contacts
              </span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">{timeAgo(a.detected_at)}</span>
              {a.acknowledged_at && (
                <Badge variant="secondary" className="text-[10px]">Acknowledged</Badge>
              )}
            </div>
            <div className="flex shrink-0 gap-1">
              <Button size="sm" variant="outline" onClick={onOpen}>
                <Eye className="h-3.5 w-3.5 mr-1.5" /> View
              </Button>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-sm leading-snug">{a.title ?? a.anomaly_type}</p>
              <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{body}</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function DetailSheet({
  anomaly, onClose, onChanged,
}: {
  anomaly: any | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const transition = useServerFn(transitionAnomaly);
  const addNote = useServerFn(addAnomalyNote);
  const fetchContacts = useServerFn(getAnomalyContacts);
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const { data: contactsData } = useQuery({
    queryKey: ["anomaly-contacts", anomaly?.id],
    queryFn: () => fetchContacts({ data: { anomaly_id: anomaly!.id } }),
    enabled: !!anomaly?.id && (anomaly?.affected_contact_ids?.length ?? 0) > 0,
  });
  const contacts: any[] = contactsData?.contacts ?? [];

  useEffect(() => { setNoteText(""); }, [anomaly?.id]);

  if (!anomaly) return null;
  const sev = SEV_STYLES[anomaly.severity] ?? SEV_STYLES.medium;
  const actions: string[] = Array.isArray(anomaly.recommended_actions) ? anomaly.recommended_actions : [];
  const notes: any[] = Array.isArray(anomaly.notes) ? anomaly.notes : [];

  const doTransition = async (action: "acknowledge" | "resolve" | "dismiss" | "reopen") => {
    setBusy(action);
    try {
      await transition({ data: { anomaly_id: anomaly.id, action } });
      const msg =
        action === "acknowledge" ? "Acknowledged" :
        action === "resolve" ? "Marked resolved" :
        action === "dismiss" ? "Dismissed" : "Reopened";
      toast.success(msg);
      onChanged();
      if (action !== "acknowledge") onClose();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setBusy(null);
    }
  };

  const submitNote = async () => {
    const body = noteText.trim();
    if (!body) return;
    setBusy("note");
    try {
      await addNote({ data: { anomaly_id: anomaly.id, body } });
      setNoteText("");
      toast.success("Note added");
      onChanged();
    } catch (e: any) {
      toast.error(e?.message ?? "Note failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open={!!anomaly} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={sev.badge}>{sev.label}</Badge>
            {anomaly.workspaces?.program_name && (
              <Badge variant="outline" className="font-normal">{anomaly.workspaces.program_name}</Badge>
            )}
            <Badge variant="secondary" className="capitalize text-[10px]">
              {anomaly.anomaly_type.replaceAll("_", " ")}
            </Badge>
            <Badge variant="outline" className="text-[10px]">{STATUS_LABEL[anomaly.status] ?? anomaly.status}</Badge>
          </div>
          <SheetTitle className="text-lg leading-snug">{anomaly.title ?? anomaly.anomaly_type}</SheetTitle>
          <SheetDescription className="text-xs">
            Detected {timeAgo(anomaly.detected_at)} · {anomaly.affected_contact_ids?.length ?? 0} contacts affected
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <section>
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">What this means</h3>
            <div className="text-sm leading-relaxed whitespace-pre-line">
              {anomaly.explanation_body ?? "Writing the brief…"}
            </div>
          </section>

          {actions.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">What you can do</h3>
              <ol className="space-y-2 list-decimal list-inside text-sm">
                {actions.map((a, i) => <li key={i} className="leading-relaxed">{a}</li>)}
              </ol>
            </section>
          )}

          {(anomaly.affected_contact_ids?.length ?? 0) > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                Affected contacts ({anomaly.affected_contact_ids.length})
              </h3>
              {contacts.length === 0 ? (
                <p className="text-xs text-muted-foreground">Loading contacts…</p>
              ) : (
                <div className="rounded border divide-y max-h-72 overflow-y-auto">
                  {contacts.map((c) => {
                    const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
                    return (
                      <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{name || ","}</div>
                          <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                        </div>
                        <div className="text-right shrink-0">
                          {c.commission_amount != null && (
                            <div className="text-xs tabular-nums">${Number(c.commission_amount).toFixed(2)}</div>
                          )}
                          {c.status && (
                            <div className="text-[10px] text-muted-foreground capitalize">{c.status}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          <section>
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Detection data</h3>
            <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto">
{JSON.stringify(anomaly.detection_payload, null, 2)}
            </pre>
          </section>

          <section>
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
              <MessageSquarePlus className="h-3.5 w-3.5" /> Notes
            </h3>
            <div className="space-y-2">
              {notes.length === 0 ? (
                <p className="text-xs text-muted-foreground">No notes yet.</p>
              ) : (
                notes.map((n, i) => (
                  <div key={i} className="rounded border bg-muted/30 p-2 text-sm">
                    <div className="text-[11px] text-muted-foreground mb-1">{timeAgo(n.created_at)}</div>
                    {n.body}
                  </div>
                ))
              )}
              <div className="flex gap-2">
                <Textarea
                  rows={2}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note for your future self…"
                  className="text-sm"
                />
                <Button size="sm" onClick={submitNote} disabled={!noteText.trim() || busy === "note"}>
                  {busy === "note" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
                </Button>
              </div>
            </div>
          </section>

          <section className="border-t pt-4 flex flex-wrap gap-2">
            {anomaly.status === "pending" ? (
              <>
                {!anomaly.acknowledged_at && (
                  <Button variant="outline" size="sm" disabled={!!busy} onClick={() => doTransition("acknowledge")}>
                    <Eye className="h-3.5 w-3.5 mr-1.5" /> Acknowledge
                  </Button>
                )}
                <Button size="sm" disabled={!!busy} onClick={() => doTransition("resolve")}>
                  <Check className="h-3.5 w-3.5 mr-1.5" /> Mark resolved
                </Button>
                <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => doTransition("dismiss")}>
                  <X className="h-3.5 w-3.5 mr-1.5" /> Dismiss
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" disabled={!!busy} onClick={() => doTransition("reopen")}>
                Reopen
              </Button>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function EmptyState({ filters }: { filters: Filters }) {
  const isPending = filters.statuses.includes("pending");
  return (
    <SharedEmptyState
      icon={Inbox}
      variant={isPending ? "first-run" : "filtered"}
      title={isPending ? "Nothing flagged right now" : "No anomalies match these filters"}
      description={isPending
        ? "Either the numbers are behaving, or there's no new signal worth surfacing. Run a scan to check."
        : "Try widening the time range or clearing severity."}
    />
  );
}


function timeAgo(iso: string | null | undefined) {
  if (!iso) return ",";
  const d = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - d) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24); if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Silence unused import (kept for parity with prior implementations using _u alias)
void _u;
